import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ErrorComponent from "@/components/ui/error";
import { Label } from "@/components/ui/label";
import LoadingScreen from "@/components/ui/loading-screen";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import { UserType } from "@/constants";
import {
  getNonRegistrantOrganizationOptions,
  getNonRegistrantOrganizationQueryKey,
  operatorDisableRegistrantUserMutation,
  operatorReenableRegistrantUserMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import {
  type Options,
  operatorTransferPrimaryOwnership,
} from "@/hera-api/client/sdk.gen";
import type {
  HttpValidationError,
  NonRegistrantOrganizationDetailResponseSchema,
  NonRegistrantRegistrantAccountDetailResponseSchema,
  OperatorTransferPrimaryOwnershipData,
} from "@/hera-api/client/types.gen";
import { isHeyApiQuery } from "@/lib/hey-api-query-key";
import { getQueryErrorHeading, getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

interface OrganizationDetailsSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string | null;
  /** When provided, clicking a secondary account opens registrant details for that account. */
  onOpenRegistrant?: (registrantId: string) => void;
  /** Operator setup: allow transferring primary ownership to an eligible secondary. */
  staffPortal?: UserType;
}

function formatValue(value: string | null | undefined): string {
  return value ?? "—";
}

function canOperatorMakePrimary(
  organization: NonRegistrantOrganizationDetailResponseSchema,
  registrant: NonRegistrantRegistrantAccountDetailResponseSchema,
): boolean {
  if (registrant.is_deleted) {
    return false;
  }
  if (registrant.id === organization.primary.id) {
    return false;
  }
  return registrant.primary_id === organization.primary.id;
}

function SecondaryAccountRow({
  registrant,
  onOpenRegistrant,
  showMakePrimary,
  onMakePrimary,
  makePrimaryDisabled,
  showAccountAction,
  onAccountAction,
  accountActionDisabled,
}: {
  registrant: NonRegistrantRegistrantAccountDetailResponseSchema;
  onOpenRegistrant?: (registrantId: string) => void;
  showMakePrimary: boolean;
  onMakePrimary: () => void;
  makePrimaryDisabled: boolean;
  showAccountAction: boolean;
  onAccountAction: () => void;
  accountActionDisabled: boolean;
}) {
  const displayName = registrant.name || registrant.email || registrant.id;
  const content = (
    <>
      <div className="font-medium">
        {displayName}
        {registrant.is_deleted ? " — Deleted" : ""}
      </div>
      {registrant.email && (
        <div className="text-sm text-muted-foreground">{registrant.email}</div>
      )}
    </>
  );
  return (
    <div className="flex flex-row gap-2 justify-between items-start py-2 border-b border-border last:border-0">
      <div className="min-w-0 flex-1">
        {onOpenRegistrant ? (
          <button
            type="button"
            onClick={() => onOpenRegistrant(registrant.id)}
            className="text-left hover:bg-muted/50 -mx-2 px-2 py-1 rounded-md transition-colors w-full"
          >
            {content}
          </button>
        ) : (
          content
        )}
      </div>
      <div className="flex shrink-0 gap-2">
        {showMakePrimary ? (
          <Button
            size="sm"
            variant="outline"
            type="button"
            disabled={makePrimaryDisabled}
            onClick={(e) => {
              e.stopPropagation();
              onMakePrimary();
            }}
          >
            Make primary
          </Button>
        ) : null}
        {showAccountAction ? (
          <Button
            size="sm"
            variant={registrant.is_deleted ? "outline" : "destructive"}
            type="button"
            disabled={accountActionDisabled}
            onClick={(e) => {
              e.stopPropagation();
              onAccountAction();
            }}
          >
            {registrant.is_deleted ? "Re-enable" : "Disable"}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function OrganizationDetailsSheet({
  isOpen,
  onOpenChange,
  organizationId,
  onOpenRegistrant,
  staffPortal,
}: OrganizationDetailsSheetProps) {
  const queryClient = useQueryClient();
  const [makePrimaryTarget, setMakePrimaryTarget] =
    useState<NonRegistrantRegistrantAccountDetailResponseSchema | null>(null);
  const [accountActionTarget, setAccountActionTarget] =
    useState<NonRegistrantRegistrantAccountDetailResponseSchema | null>(null);
  const [moveBilling, setMoveBilling] = useState(false);
  // An organization is identified by its primary account, so transferring
  // ownership moves it to a new id. Follow it instead of refetching the old one.
  // Keyed on the prop so it resets when the parent selects another organization.
  const [transferredOrganization, setTransferredOrganization] = useState<{
    from: string;
    to: string;
  } | null>(null);

  const activeOrganizationId =
    transferredOrganization?.from === organizationId
      ? transferredOrganization.to
      : organizationId;

  const operatorMakePrimaryEnabled = staffPortal === UserType.OPERATOR;

  const refreshOrganization = useCallback(async () => {
    if (!activeOrganizationId) {
      return;
    }
    await queryClient.invalidateQueries({
      queryKey: getNonRegistrantOrganizationQueryKey({
        path: { organization_id: activeOrganizationId },
        query: { include_deleted: true },
      }),
    });
    await queryClient.invalidateQueries({
      predicate: (q) =>
        isHeyApiQuery(
          q.queryKey,
          "getNonRegistrantRegistrantAccount",
          "getNonRegistrantRegistrantAccounts",
        ),
      refetchType: "all",
    });
  }, [activeOrganizationId, queryClient]);

  const disableUserMutation = useMutation({
    ...operatorDisableRegistrantUserMutation(),
    onSuccess: async () => {
      await refreshOrganization();
      toast({
        title: "User disabled",
        description:
          accountActionTarget &&
          accountActionTarget.id === organization?.payment_account_id &&
          accountActionTarget.id !== organization?.primary.id
            ? "The user can no longer log in or receive license transfers. Billing was moved to the primary account."
            : "The user can no longer log in or receive license transfers.",
        variant: "secondary",
      });
      setAccountActionTarget(null);
    },
    onError: (error: HttpValidationError) => {
      toast({
        title: "Could not disable user",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const reenableUserMutation = useMutation({
    ...operatorReenableRegistrantUserMutation(),
    onSuccess: async () => {
      await refreshOrganization();
      toast({
        title: "User re-enabled",
        description: "The registrant user can access the portal again.",
        variant: "secondary",
      });
      setAccountActionTarget(null);
    },
    onError: (error: HttpValidationError) => {
      toast({
        title: "Could not re-enable user",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const transferPrimaryMutation = useMutation({
    mutationFn: async (
      variables: Options<OperatorTransferPrimaryOwnershipData>,
    ) => {
      await operatorTransferPrimaryOwnership({
        ...variables,
        throwOnError: true,
      });
    },
    onSuccess: async (_data, variables) => {
      const newPrimaryId = variables.path?.new_primary_account_id;
      if (organizationId && newPrimaryId) {
        setTransferredOrganization({ from: organizationId, to: newPrimaryId });
      }
      if (!activeOrganizationId) {
        return;
      }
      await queryClient.invalidateQueries({
        queryKey: getNonRegistrantOrganizationQueryKey({
          path: { organization_id: activeOrganizationId },
          query: { include_deleted: true },
        }),
      });
      await queryClient.invalidateQueries({
        predicate: (q) =>
          isHeyApiQuery(
            q.queryKey,
            "getNonRegistrantOrganization",
            "getNonRegistrantOrganizations",
            "getNonRegistrantRegistrantAccounts",
          ),
        refetchType: "all",
      });
      toast({
        title: "Primary ownership transferred",
        description: "The organization primary account has been updated.",
        variant: "secondary",
      });
      setMakePrimaryTarget(null);
    },
    onError: (error: unknown) => {
      toast({
        title: "Could not transfer primary ownership",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const confirmMakePrimary = useCallback(async () => {
    if (!makePrimaryTarget || !activeOrganizationId) {
      return;
    }
    await transferPrimaryMutation.mutateAsync({
      path: {
        organization_primary_id: activeOrganizationId,
        new_primary_account_id: makePrimaryTarget.id,
      },
      body: { move_billing_to_new_primary: moveBilling },
    });
  }, [
    makePrimaryTarget,
    activeOrganizationId,
    moveBilling,
    transferPrimaryMutation,
  ]);

  const makePrimaryName = useMemo(() => {
    if (!makePrimaryTarget) {
      return "";
    }
    return (
      makePrimaryTarget.name || makePrimaryTarget.email || makePrimaryTarget.id
    );
  }, [makePrimaryTarget]);

  const {
    data: organization,
    isLoading,
    error,
  } = useQuery({
    ...getNonRegistrantOrganizationOptions({
      path: { organization_id: activeOrganizationId ?? "" },
      query: { include_deleted: true },
    }),
    enabled: isOpen && !!activeOrganizationId,
  });

  const billingAccount = organization?.registrants.find(
    (r) => r.id === organization.payment_account_id,
  );
  const primaryDisableBlocked = Boolean(
    organization &&
      !organization.primary.is_deleted &&
      organization.registrants.some(
        (r) => r.id !== organization.primary.id && !r.is_deleted,
      ),
  );
  const selectedPrimaryDisableBlocked = Boolean(
    accountActionTarget &&
      organization &&
      accountActionTarget.id === organization.primary.id &&
      primaryDisableBlocked,
  );
  const disablingSelectedBillingSecondary = Boolean(
    accountActionTarget &&
      organization &&
      accountActionTarget.id === organization.payment_account_id &&
      accountActionTarget.id !== organization.primary.id,
  );
  const disablingLastPayer = Boolean(
    accountActionTarget?.can_make_payments &&
      !accountActionTarget.is_deleted &&
      organization &&
      !organization.registrants.some(
        (registrant) =>
          registrant.id !== accountActionTarget.id &&
          !registrant.is_deleted &&
          registrant.can_make_payments,
      ),
  );
  const currentBillingName =
    billingAccount?.name || billingAccount?.email || undefined;
  // With nothing to move, the choice would be a no-op either way.
  const showBillingChoice =
    !!currentBillingName &&
    !!makePrimaryTarget &&
    organization?.payment_account_id !== makePrimaryTarget.id;

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Organization Details</SheetTitle>
            <SheetDescription className="hidden">
              Primary account and all registrants in this organization
            </SheetDescription>
          </SheetHeader>
          <div className="overflow-y-auto max-h-[calc(100vh-120px)] px-4">
            {!activeOrganizationId ? (
              <p className="text-muted-foreground text-sm">
                Select an organization to view details.
              </p>
            ) : isLoading ? (
              <div className="flex justify-center items-center py-8">
                <LoadingScreen message="Loading organization details..." />
              </div>
            ) : error ? (
              <ErrorComponent
                title={getQueryErrorHeading(error)}
                message={getQueryErrorMessage(error)}
              />
            ) : organization ? (
              <div className="w-full space-y-6">
                <Card className="gap-4">
                  <CardHeader className="flex flex-row items-start justify-between">
                    <CardTitle className="font-semibold text-xl">
                      Primary account
                    </CardTitle>
                    {operatorMakePrimaryEnabled ? (
                      <Button
                        size="sm"
                        variant={
                          organization.primary.is_deleted
                            ? "outline"
                            : "destructive"
                        }
                        type="button"
                        disabled={
                          disableUserMutation.isPending ||
                          reenableUserMutation.isPending
                        }
                        onClick={() =>
                          setAccountActionTarget(organization.primary)
                        }
                      >
                        {organization.primary.is_deleted
                          ? "Re-enable"
                          : "Disable"}
                      </Button>
                    ) : null}
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 grid-cols-2 items-start">
                      <div>
                        <p className="text-sm font-bold">Email</p>
                        <p className="text-sm">
                          {formatValue(organization.primary.email)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-bold">Name</p>
                        <p className="text-sm">
                          {formatValue(organization.primary.name)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-bold">Company name</p>
                        <p className="text-sm">
                          {formatValue(organization.primary.company_name)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-bold">Phone</p>
                        <p className="text-sm">
                          {formatValue(organization.primary.phone)}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-sm font-bold">Address</p>
                        <p className="text-sm">
                          {[
                            organization.primary.address1,
                            organization.primary.address2,
                            [
                              organization.primary.city,
                              organization.primary.state,
                              organization.primary.zipcode,
                            ]
                              .filter(Boolean)
                              .join(", "),
                          ]
                            .filter(Boolean)
                            .join(", ") || "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-bold">Status</p>
                        <p className="text-sm">
                          {organization.primary.is_deleted
                            ? "Deleted"
                            : "Active"}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="gap-4">
                  <CardHeader>
                    <CardTitle className="font-semibold text-xl">
                      Secondary Accounts (
                      {
                        organization.registrants.filter(
                          (r) => r.id !== organization.primary.id,
                        ).length
                      }
                      )
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-0">
                      {organization.registrants
                        .filter((r) => r.id !== organization.primary.id)
                        .map((r) => (
                          <SecondaryAccountRow
                            key={r.id}
                            registrant={r}
                            onOpenRegistrant={onOpenRegistrant}
                            showMakePrimary={
                              operatorMakePrimaryEnabled &&
                              canOperatorMakePrimary(organization, r)
                            }
                            onMakePrimary={() => setMakePrimaryTarget(r)}
                            makePrimaryDisabled={
                              transferPrimaryMutation.isPending
                            }
                            showAccountAction={operatorMakePrimaryEnabled}
                            onAccountAction={() => setAccountActionTarget(r)}
                            accountActionDisabled={
                              disableUserMutation.isPending ||
                              reenableUserMutation.isPending
                            }
                          />
                        ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={makePrimaryTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setMakePrimaryTarget(null);
          }
          setMoveBilling(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Make primary account</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-muted-foreground text-sm">
                <p>
                  Transfer organization primary ownership to{" "}
                  <strong>{makePrimaryName}</strong>? The current primary
                  becomes a secondary account linked to that user.
                </p>
                <p className="font-semibold text-destructive">
                  This should only be done when the registrants agree; reversing
                  it requires another transfer.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          {showBillingChoice ? (
            <div className="space-y-3">
              <div className="space-y-1">
                <h3 className="font-medium text-sm">Stripe billing account</h3>
                <p className="text-muted-foreground text-sm">
                  The billing account is the organization member whose Stripe
                  customer is charged for card and ACH payments. Saved cards and
                  bank accounts never move between members.
                </p>
              </div>
              <RadioGroup
                value={moveBilling ? "move" : "keep"}
                onValueChange={(value) => setMoveBilling(value === "move")}
              >
                <div className="flex items-start gap-2">
                  <RadioGroupItem
                    value="keep"
                    id="operator-billing-keep"
                    className="mt-1"
                  />
                  <Label
                    htmlFor="operator-billing-keep"
                    className="font-normal"
                  >
                    Keep billing with <strong>{currentBillingName}</strong>, who
                    keeps paying even after becoming a secondary user.
                  </Label>
                </div>
                <div className="flex items-start gap-2">
                  <RadioGroupItem
                    value="move"
                    id="operator-billing-move"
                    className="mt-1"
                  />
                  <Label
                    htmlFor="operator-billing-move"
                    className="font-normal"
                  >
                    Bill <strong>{makePrimaryName}</strong> instead. Payment
                    methods saved by {currentBillingName} stay on their Stripe
                    customer and will not be available at checkout.
                  </Label>
                </div>
              </RadioGroup>
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={transferPrimaryMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              type="button"
              onClick={() => void confirmMakePrimary()}
              disabled={transferPrimaryMutation.isPending}
              className="bg-destructive hover:bg-destructive/80 text-destructive-foreground"
            >
              {transferPrimaryMutation.isPending ? "Transferring…" : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={accountActionTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setAccountActionTarget(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {selectedPrimaryDisableBlocked
                ? "Cannot disable primary account"
                : accountActionTarget?.is_deleted
                  ? "Re-enable registrant user"
                  : "Disable registrant user"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedPrimaryDisableBlocked
                ? `This is a primary account and there are active secondary accounts for ${organization?.primary.company_name || organization?.primary.name || organization?.primary.email}. Please disable the secondary accounts first.`
                : accountActionTarget?.is_deleted
                  ? `Restore portal access for ${accountActionTarget.name || accountActionTarget.email}?`
                  : `Disable ${accountActionTarget?.name || accountActionTarget?.email}? They will no longer be able to log in or receive license transfers. Existing licenses and history will be preserved.${disablingSelectedBillingSecondary ? " This is the billing account, so billing will move to the primary account." : ""}${disablingLastPayer ? " This user is the only person currently allowed to make payments, so the primary user will be given payment permission." : ""}`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {selectedPrimaryDisableBlocked ? (
              <AlertDialogCancel>Close</AlertDialogCancel>
            ) : (
              <>
                <AlertDialogCancel
                  disabled={
                    disableUserMutation.isPending ||
                    reenableUserMutation.isPending
                  }
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  type="button"
                  className={
                    accountActionTarget?.is_deleted
                      ? undefined
                      : "bg-destructive hover:bg-destructive/80 text-destructive-foreground"
                  }
                  disabled={
                    disableUserMutation.isPending ||
                    reenableUserMutation.isPending
                  }
                  onClick={(event) => {
                    event.preventDefault();
                    if (!accountActionTarget) {
                      return;
                    }
                    const variables = {
                      path: { registrant_account_id: accountActionTarget.id },
                    };
                    if (accountActionTarget.is_deleted) {
                      reenableUserMutation.mutate(variables);
                    } else {
                      disableUserMutation.mutate(variables);
                    }
                  }}
                >
                  {disableUserMutation.isPending ||
                  reenableUserMutation.isPending
                    ? "Saving…"
                    : accountActionTarget?.is_deleted
                      ? "Re-enable"
                      : "Disable"}
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
